import React, { useMemo, useEffect, useState } from 'react';
import { Box, Html } from '@react-three/drei';
import * as THREE from 'three';
import { tokens } from '@fluentui/react-components';
import { getGrainBumpTexture } from '../utils/grainTexture';
import { OverlayBtn, overlayWrapStyle } from './SingleCabinet3D';
import { HoverEdgeFrame, PanelDepthTooltip } from '../utils/edgeHover.jsx';

// --- "Stogelis" (viršutinė dengiančia plokštė) 3D atvaizdavimas ---
// ClickUp "2.1 Baldas. Stogelis. Visi pasirinkimai." — pridėtas stogeliai.zip su
// kiekvieno iš 9 variantų brėžiniais (du vaizdai kiekvienam: priekis + 3D kampas).
// Tipų id/pavadinimai atitinka configPanelConfig.js/TopPanel.jsx `options` (ir
// assets/img/icons/stogeliai/1-9.jpg piktogramas):
//   1 Tarp šonų        — plokštė ĮLEISTA tarp šoninių sienelių (neuždengia jų viršaus)
//   2 Ant šonų         — plokštė LYGI su korpuso pločiu (dengia šonines sieneles iš
//                        viršaus), JOKIO pločio overhang — baldo plotis = korpuso plotis
//   3 Dvigubas stogelis— VIRŠUTINĖ plokštė kaip (2) (flush, lygi su korpuso pločiu),
//                        PLIUS ANTRA, APATINĖ plokštė tiesiog po pirmąja — BET ši
//                        apatinė plokštė yra STRUKTŪRINĖ (ne dekoratyvinė): įleista
//                        tarp šonų (kaip tipo 1), ir UŽ durelių, be FRONT_OVERHANG
//                        (kaip tipų 5/6/7) — ClickUp "Stogelis (dvigubas). Apatinė
//                        plokštė tarp šonų ir mažesnio gylio".
//   4 Karūna / Cokolis — PAGRINDINĖ (apatinė) danga — STRUKTŪRINĖ plokštė, įleista
//                        tarp šonų ir UŽ durelių (ta pati taisyklė, kaip (3) apatinė
//                        plokštė aukščiau — ClickUp "Stogelis (Karūna/Cokolis).
//                        Apatinė plokštė tarp šonų ir mažesnio gylio"), PLIUS TRYS
//                        statmenos juostos ANT jos viršaus — viena priekyje, dvi
//                        šonuose (U forma, atvira gale), kiekviena pakelta per
//                        `height` — juostos TOLIAU remiasi į TIKRĄ/pilną korpuso
//                        plotį/gylį (dekoratyvinis, matomas rėmas prie tikro krašto,
//                        ne struktūrinė danga).
//   5 Nuleistas        — plokštė kaip (1) (ĮLEISTA tarp šonų), bet pastumta žemyn
//                        per `height` — šonai (piešiami kitur) natūraliai kyšo
//                        aukščiau už įleistą plokštę
//   6 Nuleistas kairys — VIENA ištisinė plokštė, nuleista per `height` VISU ilgiu
//                        (kaip (5)). PLOTIS dabar SIMETRIŠKAS — ĮLEISTAS tarp šonų
//                        ABIEM pusėm (ta pati taisyklė, kaip (1)/(5), žr. ClickUp
//                        "Stogelis (nuleistas k/d)... Plotis tarp šonų (116.4cm)") —
//                        PATAISYTA (4-as kartas): ankstesnė (3x koreguota) versija
//                        plokštę darė ASIMETRIŠKO pločio (kairėje flush, dešinėje
//                        įleista), bet vartotojas galiausiai patvirtino, kad PATI
//                        plokštė turi būti simetriška kaip (1)/(5) — vienintelė
//                        asimetrija ("kairys"/"dešinys" tipo pavadinime) kyla iš
//                        TIKROS IŠORINĖS šoninės sienelės aukščio (žr.
//                        SingleCabinet3D.jsx leftOuterFlushDrop/rightOuterFlushDrop,
//                        NEPALIESTA šiuo pataisymu) — tipo 6 atveju kairė sienelė PATI
//                        sutrumpėja iki nuleistos plokštės lygio (susilieja be
//                        tarpo), dešinė lieka pilno aukščio (kyšo virš nuleistos
//                        plokštės — tai ir yra "nuleidimo" efektas TOJE pusėje).
//   7 Nuleistas dešinys— kaip (6), veidrodinis WALL-drop prasme (žr.
//                        rightOuterFlushDrop) — PATI plokštė IDENTIŠKA (6)/(5),
//                        simetriška, žr. aukščiau.
//   8 Ant dešinio šono — geometriškai TAS PATS, kas (2) — lygi su korpuso pločiu,
//                        JOKIO overhang. PATAISYTA (3x — galutinai, žr. vartotojo
//                        tiesioginį paaiškinimą): Stogelis NIEKADA negali kyšoti už
//                        PAČIO baldo šoninių sienelių — "turi būti nuo kairės baldo
//                        pusės plokštės iki dešinės, o ne išsikišti". Papildomą plotį
//                        baldui prideda IŠIMTINAI atskiras "Kairys šonas"/"Dešinys
//                        šonas" funkcionalumas (žr. SideElementDialog.jsx,
//                        leftSideElementWidth/rightSideElementWidth) — TA skirtinga,
//                        nesusijusi savybė, ne Stogelio tipas. (Anksčiau čia buvo
//                        bandytas vienpusis overhang pagal assets/img/icons/
//                        stogeliai/8.jpg piktogramą — piktograma rodo overhang, bet
//                        vartotojas GYVAI patikrino rezultatą ir paaiškino, kad tai
//                        neteisinga šiam produktui; ZIP "Stogelis8" brėžinys (be
//                        overhang) buvo teisingas visą laiką.)
//   9 Ant kairio šono  — tas pats, kas (8)/(2) — žr. aukščiau.
//
// Visos plokštės — TOS PAČIOS plokštės, kaip ir likusi baldo dalis (ne atskira
// "medinė" detalė): storis visada safeWallThickness (realus pasirinktos medžiagos
// storis), spalva/tekstūra — `color` props, TA PATI kaip korpuso (žr. TopMat
// žemiau). Stogelio TIPAS čia aprašo TIK šių plokščių IŠSIDĖSTYMĄ (poziciją,
// ar yra antra plokštė ir pan.), ne naują medžiagą ar storį. JOKS tipas neprideda
// PLOČIO už korpuso ribų — tik 1/5 (tarp šonų) yra siauresni už korpusą.

const FRONT_OVERHANG = 0.02;
// Konservatyvi, nuo realaus wallThickness nepriklausoma paraštė kolizijos/sienos
// aukščio ribojimo skaičiavimui (getTopPanelExtraBounds žemiau neturi prieigos
// prie wallThickness — ją gauna tik pats 3D komponentas) — visada ŠIEK TIEK
// didesnė už bet kokį realų plokštės storį, tad niekada nepavertina ribos.
const CAP_MARGIN = 0.03;

// Kiek Stogelis IŠKYŠA už nominalaus baldo gabarito (totalWidth/totalHeight/
// totalDepth) — naudojama koliziją/sienos aukščio ribojimą skaičiuojančio kodo
// (FurnitureGroup.jsx), kad baldas su Stogeliu "įtilptų" į savo matmenis, o ne
// tyliai kyšotų pro lubą/sieną, apie kurią sistema nieko nežino (žr. vartotojo
// pastabą: "Stogelis yra kartais baldo dalis ir turi įtilpti į jo matmenis").
// Tipas 4 vienintelis prideda AUKŠTĮ (karūnos juosta pakyla virš korpuso viršaus,
// per `height`) — visi kiti arba lygūs su viršumi, arba nuleisti (niekada
// nekyšo aukščiau korpuso). PLOČIO (width) overhang NEBĖRA jokiam tipui (žr. failo
// viršaus komentarą — Stogelis niekada nekyšo už baldo šoninių sienelių; papildomą
// plotį prideda tik atskiras "Kairys/Dešinys šonas" funkcionalumas) — tik gylis
// (priekinis FRONT_OVERHANG) lieka. CAP_MARGIN (3cm) jau viršija bet kokį galimą
// "Ištraukti į priekį"/karkaso poslinkį (±safeWallThickness, žr. topDepthDelta
// žemiau), tad šis ribojimas nekeičiamas dėl perjungiklio.
export const getTopPanelExtraBounds = (type, height = 0.1) => {
  if (!type || type === 0) return { width: 0, height: 0, depth: 0 };
  const safeHeight = Math.max(0.01, Number(height) || 0.1);
  const extraHeight = type === 4 ? safeHeight + CAP_MARGIN : CAP_MARGIN;
  // VISI tipai (1-9) nebeprideda FRONT_OVERHANG prie gylio (žr. `d`/`dPosZ` žemiau
  // — ClickUp "Stogeliams irgi turi galioti ta pati taisyklė dėl plokščių karkaso
  // plokštumoje ar ne, priklausomai nuo durelių" — vartotojas PATVIRTINO, kad tai
  // galioja ir 3/4 viršutinei/pagrindinei dangai: "Turi būti priklausoma nuo
  // durelių" — atšaukia ankstesnį sprendimą palikti juos nepaliestus). Ši riba
  // turi atitikti TIKRĄ geometriją, kitaip koliziją/sienos skaičiavimas
  // (FurnitureGroup.jsx) rezervuotų nereikalingą 2cm tarpą, kurio plokštė realiai
  // nebeužima.
  const depth = 0;
  return { width: 0, height: extraHeight, depth };
};

// Ta pati "vienoda spalva be tekstūros" logika, kaip CabinetPanel (SingleCabinet3D.jsx)
// naudoja likusiems korpuso skydams — Stogelis TURI atrodyti kaip TA PATI viršutinė
// plokštė, ne kaip atskiras medžio spalvos elementas. Anksčiau čia buvo fiksuota
// WOOD_COLOR ('#c99a5b') — vartotojas paaiškino, kad stogeliai/1-9.jpg piktogramose
// medžio spalva naudojama TIK diagramos aiškumui (kad matytųsi, kaip plokštės
// susijungia), o ne kaip nurodymas realiam baldui naudoti kitą medžiagą (žr.
// vartotojo "paveiksliuke tik vizualiai jie yra medžio spalvos, tik kad būtų
// aiškiau kaip plokštės susideda").
// isFacade (polygonOffset -8/-8) — TIKSLIAI ta pati reikšmė, kaip CabinetPanel/
// DividerDragHandle naudoja SAVO "Ištraukta į priekį" medžiagai (SingleCabinet3D.jsx).
// Be šito Stogelis (bet koks tipas), pažymėtas "Ištraukti į priekį", atsidurdavo
// LYGIAI tame pačiame Z gylyje, kaip ir durelės po juo — GPU atsitiktinai rinkdavosi,
// kurį paviršių rodyti (z-fighting), vartotojui atrodydavo kaip "didelis šešėlis ant
// durelių" arba vietomis permatomas paviršius (vartotojas: "kodel jeigu istraukta
// plokste tai ant dureliu krenta toks didelis seselis ir kaikuriais atvejais
// atsiranda permatomumas"). DoorFaceMaterial (OuterDoors.jsx) savo pusėje visada
// naudoja -4/-4 — Stogelio -8/-8 (neigiamesnis) užtikrina, kad JIS visada laimi ir
// lieka matomas, ta pati konvencija, kaip jau veikia kiekvienai kitai šio baldo
// plokštei, kuri gali atsidurti tame pačiame gylyje kaip durelės.
const TopMat = ({ color, isFacade = false }) => (
  <meshStandardMaterial
    color={color}
    bumpMap={getGrainBumpTexture()} bumpScale={0.0018}
    roughness={0.34} metalness={0} envMapIntensity={0}
    // metalness 0.06→0, envMapIntensity (nebuvo nurodyta, t.y. numatytoji 1.0) →0.2 —
    // žr. OuterDoors.jsx DoorFaceMaterial komentarą: kameros-kampo priklausomas
    // Fresnel/Environment IBL patamsėjimas paviršiaus kraštuose.
    // Bendra ryškumo "grindis" (0.3) PAČIA Stogelio spalva — žr. identišką
    // SingleCabinet3D.jsx CabinetPanel komentarą: įgaubto 90° kampo (Stogelis vs
    // šoninė siena) patamsėjimas, vartotojo nebepriimtas net po algebra įrodyto
    // nulinio tarpo kitur šiame fasade.
    emissive={color} emissiveIntensity={0.3}
    // -8/-8 → -2/-2 — žr. SingleCabinet3D.jsx CabinetPanel / OuterDoors.jsx DoorFaceMaterial
    // identišką komentarą: mažesnė reikšmė tebesprendžia koplanarumą su durimis, bet su
    // mažesniu šalutinio poveikio spinduliu kaimyninėms, offset'o neturinčioms riboms.
    {...(isFacade ? { polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 } : null)}
  />
);

// /admin/bugs "absoliuciai visos stogelio plokstes turi tureti konturus (kaip
// dabar yra su kitom plokstem)" — ta pati "Rodyti kontūrus" priekinio paviršiaus
// linijos logika, kaip CabinetPanel/UnifiedBase.jsx PanelEdges (žr. ten identišką
// komentarą apie z+0.001 paraštę, saugančią nuo z-fighting, ir kodėl tik priekinis
// paviršius, ne visos 6 briaunos).
const PanelEdges = ({ args, position }) => {
  const obj = useMemo(() => {
    const [w, h, d] = args;
    const z = d / 2 + 0.001;
    const pts = new Float32Array([
      -w / 2, -h / 2, z,
       w / 2, -h / 2, z,
       w / 2,  h / 2, z,
      -w / 2,  h / 2, z,
    ]);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pts, 3));
    const mat = new THREE.LineBasicMaterial({ color: '#1a1a1a' });
    const loop = new THREE.LineLoop(geo, mat);
    loop.raycast = () => {};
    return loop;
  }, [args[0], args[1], args[2]]);
  useEffect(() => () => { obj.geometry.dispose(); obj.material.dispose(); }, [obj]);
  return <primitive object={obj} position={position} />;
};

// Bendras Box+medžiaga+(pasirinktinai)kontūras vienetas, naudojamas VISOMS šio
// failo plokštėms — išvengia tos pačios <Box><TopMat/></Box>{showEdges&&<PanelEdges/>}
// trijulės kartojimo 9 kartų (kai kurie tipai turi kelias plokštes, pvz. 3/4).
// Dabar TAKŽE gauna tą pačią select/"Ištraukti į priekį" mechaniką, kaip CabinetPanel
// (SingleCabinet3D.jsx) ir FrontTrimBox (UnifiedBase.jsx) — žr. UnifiedTopPanel
// komentarą žemiau prie topElemKey. isSelected/isFacade/onSelectSelf yra BENDRI
// visoms šio Stogelio dėžėms (kaip UnifiedBase "Kojelės" — visos kartu sudaro VIENĄ
// pažymimą/perjungiamą vienetą), bet kiekviena šviečia individualiai užvedus pelę,
// ir mygtukas (showToggleButton) rodomas TIK prie pagrindinės (dangos) plokštės,
// kad nesikartotų prie kiekvienos dalies.
// SKIRTINGAI nuo CabinetPanel/FrontTrimBox (kurie naudoja isNearPanelEdge — click
// veikia TIK arti plokštės krašto, žr. edgeHover.jsx): čia VISAS paviršius yra
// hover/click zona, be jokio "tik prie krašto" apribojimo. Priežastis: tas
// apribojimas skirtas PLONOMS, vertikalioms, dažnai VIENA PRIE KITOS besiribojančioms
// plokštėms (karkaso sienelės/pertvaros), kur reikia atskirti, PRIE KURIOS tiksliai
// plokštės krašto spustelėta. Stogelio dangos plokštė yra IZOLIUOTA, vienintelis
// elementas tame aukštyje — jokios kaimyninės plokštės, su kuria supainioti, tad
// edge-only apribojimas čia tik be reikalo apsunkina paspaudimą (plati, horizontali
// plokštė — vartotojas natūraliai spaudžia VIDURĮ, ne siaurą ~3cm pakraštį, žr.
// EDGE_MARGIN_M) — vartotojas: "paspaudus ant stogelio plokstes man neatsiranda
// mygtukas".
const TopBox = ({
  args, position, color, showEdges, activeStep,
  isFacade = false, isSelected = false, onSelectSelf = null,
  onToggleFront = null, showToggleButton = false,
}) => {
  const [hovered, setHovered] = useState(false);
  const [depthHovered, setDepthHovered] = useState(false);
  const isIsore = activeStep === 'Išorė';
  const isHoverable = isIsore && !!onSelectSelf;
  const hoverHandlers = {
    // setDepthHovered — žr. PanelDepthTooltip importo komentarą edgeHover.jsx faile:
    // informacinis "Gylis" tooltip'as veikia nepriklausomai nuo isHoverable.
    onPointerMove: (e) => {
      setDepthHovered(true);
      if (!isHoverable) return;
      e.stopPropagation();
      if (!hovered) { setHovered(true); document.body.style.cursor = 'pointer'; }
    },
    onPointerOut: () => { setDepthHovered(false); setHovered(false); if (isHoverable) document.body.style.cursor = ''; },
    onClick: isIsore && onSelectSelf ? (e) => { e.stopPropagation(); onSelectSelf(); } : undefined,
  };
  // Pati plokštė yra VOS safeWallThickness (~1.8cm) storio Y ašimi — iš dažno žiūrėjimo
  // kampo (beveik statmenai iš viršaus/priekio) jos raycastinamas "pjūvis" ekrane
  // suplokštėja iki vos kelių pikselių, tad net spustelėjus TIKSLIAI ten, kur plokštė
  // vizualiai matoma, spindulys dažnai jos "nepagauna" (patikrinta rankiniu spindulio
  // skaičiavimu: tas pats X, Y tik per kelis cm skirtingas, kartais visai nieko
  // neatitinka). Ta pati problema ir TAS PATS sprendimas, kaip CabinetPanel/
  // FrontTrimBox ištemptas nematomas "hit" Box (žr. ten komentarus) — tik čia
  // ištempiama Y (aukštis), ne Z (gylis), nes čia plokštė plona BŪTENT Y ašimi.
  //
  // vartotojas: "nepavyksta paspausti... iskaitant ir baldo virsu" — ŠITO konkretaus
  // TopBox atvejo (Stogelio tipas pasirinktas) gyvai patikrinti nepavyko šios sesijos
  // metu (testuoto baldo topPanelType buvo 0 — Stogelis apskritai nerenderinamas), bet
  // ANALOGIŠKAS, TIKRAI patvirtintas atvejis (UnifiedBase.jsx FrontTrimBox prieš dureles/
  // "Bottom wall" skydą, žr. ten komentarą) turėjo TIKSLIAI tą pačią geometrinę priežastį
  // (status kameros kampas, kaimyno Z ašimi ištemptas hitbox) ir buvo išspręstas šia
  // pačia technika. +0.06 čia jau buvo PATS kodas pripažinęs problemą (žr. komentarą
  // aukščiau) — padidinta iki +0.2 Y ir pridėta +0.07 Z (ta pati norma, kaip CabinetPanel
  // "top" sienos savo hitDepthArg), kad TopBox turėtų realias šansas laimėti tą pačią
  // klasę konkurencijos, PREVENCIŠKAI (nepatikrinta šiuo konkrečiu atveju tiesiogiai).
  const hitHeight = args[1] + 0.2;
  const hitDepth = args[2] + 0.07;
  const hitPosZ = (hitDepth - args[2]) / 2;
  return (
    <group position={position}>
      <Box args={args}>
        <TopMat color={color} isFacade={isFacade} />
      </Box>
      <Box args={[args[0], hitHeight, hitDepth]} position={[0, 0, hitPosZ]} {...hoverHandlers}>
        <meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </Box>
      {showEdges && <PanelEdges args={args} position={[0, 0, 0]} />}
      <HoverEdgeFrame width={args[0]} height={args[1]} depth={args[2]} visible={isHoverable && (hovered || isSelected)} />
      <PanelDepthTooltip depth={args[2]} position={[0, 0, args[2] / 2 + 0.02]} visible={depthHovered} />
      {showToggleButton && isIsore && isSelected && onToggleFront && (
        <Html position={[0, 0, args[2] / 2 + 0.02]} center zIndexRange={[175, 0]} style={{ pointerEvents: 'none' }}>
          <div style={overlayWrapStyle}>
            <OverlayBtn
              title={isFacade ? "Grąžinti į karkaso plokštumą" : "Ištraukti į priekį (fasadų plokštuma)"}
              onClick={(e) => { e.stopPropagation(); onToggleFront(); }}
            >
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300, color: isFacade ? '#09aa89' : undefined }}>
                swap_horiz
              </span>
            </OverlayBtn>
          </div>
        </Html>
      )}
    </group>
  );
};

const UnifiedTopPanel = ({
  type, totalWidth, totalDepth, wallThickness, topY, height = 0.1, color = '#d6d6d6', showEdges = false,
  activeStep,
  // Bendras, elemento-id pagrįstas pažymėjimo/fasado mechanizmas — ta pati
  // furnitureId + raktų schema, kaip UnifiedBase.jsx elemProps (outerpanel:
  // ${furnitureId}-topcanopy:top / ${furnitureId}-topcanopy-top), tik su
  // "topcanopy" kaip pastoviu "cabinetIndex", kad niekada nesusikirstų su
  // tikrais kabinetų indeksais ar UnifiedBase "base" raktu.
  furnitureId = null, selectedDividerKey = null, onDividerSelect = null,
  panelFrontOffsets = null, onTogglePanelFront = null,
  // ClickUp "3.0 Išorė. Karkaso gylis. Durų uždėjimo/nuėmimo taisyklė" — Stogelis
  // (kaip ir karkaso šonai/pagrindas, žr. SingleCabinet3D.jsx carcassPanelToggleProps)
  // TURI sugilėti TIK kai baldas turi bent vienas dureles, ne visada — anksčiau
  // topDepthDelta žemiau sugildavo BE SĄLYGŲ (kiekvienam baldo, su durim ar be),
  // t.y. durų-be baldas rodydavo 58,2cm formato gylį, kai turėtų 60cm. Default true
  // (sena, visada-įgilinta elgsena), jei kviečiantysis neperduoda.
  anyDoorInGroup = true,
  // FurnitureGroup.jsx dabar gali iškviesti ŠĮ komponentą KELIS kartus — po vieną
  // kiekvienam gretimų kabinetų RUN'ui, turinčiam TĄ PATĮ aukštį (žr. ten
  // topPanelRuns) — vartotojas: "stogelio pasirinkimus... logika butu pritaikoma
  // kiekvienam skyriui atskirai, kai baldas turi daugiau nei viena skyriu, nes
  // dabar stogelis kabo ore ten kur skyriu aukstis yra mazesnis" (anksčiau VISADA
  // buvo TIK vienas kvietimas su topY=totalHeight, t.y. AUKŠČIAUSIO kabineto
  // aukščiu — žemesni kabinetai likdavo su tarpu po danga). Kiekvienas run'as
  // perduoda SAVO totalWidth (tik savo kabinetų plotis) ir topY (SAVO aukštis),
  // bet tipų 1/5/6/7 kraštų įleidimas (flush/inset) prasmingas TIK ties TIKRU
  // viso baldo kraštu, ne ties vidiniu run'ų tarpusavio susiliejimu — šios dvi
  // vėliavėlės sako, ar ŠIS kvietimas apima atitinkamą TIKRĄ kraštą.
  isGroupLeftEdge = true, isGroupRightEdge = true,
}) => {
  const MIN_DIMENSION = 0.001;
  const safeTotalWidth = Math.max(MIN_DIMENSION, Number(totalWidth) || 0);
  const safeDepth = Math.max(MIN_DIMENSION, Number(totalDepth) || 0);
  const safeWallThickness = Math.max(MIN_DIMENSION, Number(wallThickness) || 0.018);
  const safeTopY = Number(topY) || 0;
  const safeHeight = Math.max(0.01, Number(height) || 0.1);
  const halfW = safeTotalWidth / 2;
  // ClickUp "2.1 Baldai. Stogelis (nuleistas)/(Ant šonų)... stogelis už durelių" —
  // FRONT_OVERHANG (2cm dekoratyvinis priekinis iškyšulys) anksčiau buvo pridedamas
  // BE SĄLYGŲ net tipams 2/5/6/7, kurie turi būti PASLĖPTI UŽ durelių, ne kyšoti
  // priekyje — tai beveik panaikindavo jau esantį -safeWallThickness atitraukimą į
  // karkaso plokštumą (žr. topDepthDelta žemiau), paliekant tik +0,2cm PRIEŠ dureles
  // vietoj -1,8cm UŽ jų. Iš pradžių pataisyta tik 5/6/7 (patvirtinta "Plokščių
  // koordinatės" įrankiu: Z max = 60,2 vietoj 58,2, kurį rodo kiti UŽ durelių
  // atitraukti skydai). Vėliau ClickUp "Stogelis (Ant šonų)" parodė, kad kai joks
  // tipas nepasirinktas, numatytasis atvaizdavimas jau rodo 58,2 — bet pasirinkus
  // TIPĄ 2 gylis netikėtai šoka į 60,2 — t.y. 2 turėjo tą pačią taisyklę, kaip
  // 5/6/7, tiesiog ji buvo nepatikrinta/nepritaikyta.
  // PATAISYTA (3-as raundas) — ClickUp "Stogeliams irgi turi galioti ta pati
  // taisyklė dėl plokščių karkaso plokštumoje ar ne, priklausomai nuo durelių...
  // kaikuriais atvejais stogelio gylis būna 62cm" (tipas 1): topDepthDelta
  // (žemiau) nuo šios sesijos jau sąlygiškas pagal anyDoorInGroup (0 arba
  // -wallThickness) — be durelių jis duoda 0, o tipas 1 (į FRONT_OVERHANG sąrašą
  // NIEKADA nebuvo įtrauktas, nes jo užduotis anksčiau lietė TIK aukštį, ne gylį)
  // vis tiek pridėdavo +2cm iškyšulį, duodamas 60+2+0=62cm vietoj 60cm. Tipai 8/9
  // (geometriškai TAS PATS, kas 2) turėjo TĄ PATĮ, dar nepastebėtą trūkumą.
  // 4-as raundas — tipų 3/4 VIRŠUTINĖ/pagrindinė danga iš pradžių sąmoningai
  // palikta nepaliesta (anksčiau patvirtinta su durimis rodant 60,2cm), bet
  // vartotojas PATVIRTINO, kad taisyklė turi galioti IR jai: "Turi būti priklausoma
  // nuo durelių" — t.y. ta 60,2cm reikšmė buvo teisinga TIK TODĖL, kad furnitūra
  // tuo metu turėjo dureles (58,2+FRONT_OVERHANG=60,2 su tipo 5/6/7 logika; dabar
  // be durelių teisingai duos 60cm, ne 62cm). VISIEMS tipams (1-9) iškyšulys dabar
  // praleidžiamas visai — stogelis lieka TOJE PAČIOJE karkaso plokštumoje, kaip bet
  // kuris kitas bordersDoor skydas. Tipo 4 rizerai (dEff/dPosZEff naudoja tiesiogiai)
  // NELIEČIAMI atskirai — jie TURI sekti pagrindinės dangos priekinį paviršių
  // (kad liktų su juo susilieję), tad natūraliai perima tą patį pokytį, kaip ir
  // dera dekoratyviam rėmui, prigludusiam prie dangos krašto.
  // FRONT_OVERHANG (žr. failo viršų) daugiau NIEKAM nebetaikomas — konstanta
  // palikta tik dėl istorinių komentarų aukščiau, pačioje formulėje jos nebėra.
  const d = safeDepth;
  const dPosZ = 0;

  if (!type || type === 0) return null;

  // Kiekvieno tipo "norimas" įleidimas nuo TIKRO baldo krašto (nepriklauso nuo to,
  // kuris run'as šiuo metu piešiamas) — ta pati reikšmė, kaip anksčiau atskirai
  // užrašyta kiekvienoje type===N šakoje, dabar surinkta vienoje vietoje, kad
  // run'ų atveju būtų taikoma TIK ten, kur run'as realiai liečia tą TIKRĄ kraštą
  // (žr. isGroupLeftEdge/isGroupRightEdge aukščiau) — vidinė run'ų riba visada
  // lieka LYGI (0 įleidimo), kad gretimi run'ai susilietų be tarpo/laiptelio.
  // ClickUp "Stogelis (nuleistas k/d). Plotis tarp šonų (116.4cm)" — tipai 6/7
  // PATI plokštė dabar SIMETRIŠKA, kaip 1/5 (žr. header komentarą aukščiau) — vienintelė
  // 6-vs-7 asimetrija yra WALL-drop (leftOuterFlushDrop/rightOuterFlushDrop
  // SingleCabinet3D.jsx), ne šis plokštės plotis.
  let desiredLeftInset = 0, desiredRightInset = 0;
  if (type === 1 || type === 5 || type === 6 || type === 7) { desiredLeftInset = safeWallThickness; desiredRightInset = safeWallThickness; }
  const leftInset = isGroupLeftEdge ? desiredLeftInset : 0;
  const rightInset = isGroupRightEdge ? desiredRightInset : 0;
  const w = Math.max(MIN_DIMENSION, safeTotalWidth - leftInset - rightInset);
  const cx = (leftInset - rightInset) / 2;
  // 5/6/7 visi "nuleidžia" plokštę per height — vienintelis skirtumas nuo 1 yra šis
  // Y poslinkis.
  const yDrop = (type === 5 || type === 6 || type === 7) ? safeHeight : 0;

  // Stogelio plokštė dabar gauna TĄ PATĮ "Ištraukti į priekį (fasadų plokštuma)" /
  // "Grąžinti į karkaso plokštumą" perjungiklį, kokį jau turi visos kitos šio baldo
  // plokštės (žr. SingleCabinet3D.jsx CabinetPanel bordersDoor/isFacade ir
  // UnifiedBase.jsx FrontTrimBox facadeShift) — vartotojas: "Stogelio plokstem irgi
  // reikia istraukti i prieki (fasadu plokstuma) ir Grazinti i karkaso plokstuma
  // funkcionalumo, kaip ir visose kitose baldo plokstese". VISA Stogelio plokštė
  // (nepriklausomai nuo tipo/kiek dėžių ją sudaro) sudaro VIENĄ pažymimą/perjungiamą
  // vienetą (kaip UnifiedBase "Kojelės") — kiekviena dalis paspaudžiama (pažymi tą
  // pačią bendrą būseną), bet mygtukas rodomas tik prie pagrindinės dangos plokštės.
  //
  // Numatytoji (neperjungta) būsena dabar ATITRAUKTA per safeWallThickness atgal į
  // karkaso plokštumą nuo anksčiau VIENINTELĖS egzistavusios (fiksuotos) pozicijos —
  // ta pati konvencija, kaip VISOS kitos šio baldo plokštės, kurios DEFAULT'u stovi
  // karkaso, ne fasado, plokštumoje, KAI YRA durelių (CabinetPanel bordersDoor;
  // UnifiedBase frontCenterZ). Perjungus į fasadą, plokštė pasislenka į priekį (ta
  // pati +0.006 paraštė, kuri kitose plokštėse skiria nuo durų priekinio paviršiaus),
  // t.y. grįžta lygiai į seną, visada buvusią vietą. FRONT_OVERHANG (dekoratyvinis
  // iškyšulys į priekį, žr. aukščiau) išlieka nepaliestas abiem atvejais — tai
  // ATSKIRAS, su durų/karkaso klausimu nesusijęs dizaino elementas.
  const topElemKey = `outerpanel:${furnitureId}-topcanopy:top`;
  const topIsFacade = panelFrontOffsets?.[`${furnitureId}-topcanopy-top`] === 'facade';
  const topIsSelected = selectedDividerKey === topElemKey;
  const topOnSelectSelf = onDividerSelect ? () => onDividerSelect(selectedDividerKey === topElemKey ? null : topElemKey) : null;
  const topOnToggleFront = onTogglePanelFront ? () => onTogglePanelFront(furnitureId, 'topcanopy', 'top') : null;
  // ClickUp "Durų uždėjimo/nuėmimo taisyklė (karkaso gylis)" — žr. anyDoorInGroup
  // komentarą aukščiau: recede (−safeWallThickness) TIK kai yra durelių, kitaip 0
  // (ta pati TAISYKLĖ, kaip CabinetPanel depthDelta — tik ŠIO komponento isFacade
  // atvejo poslinkis yra +0,006, ne 0, tad jis paliktas nepaliestas).
  const topDepthDelta = topIsFacade ? 0.006 : (anyDoorInGroup ? -safeWallThickness : 0);
  const dEff = d + topDepthDelta;
  const dPosZEff = dPosZ + topDepthDelta / 2;
  const toggleProps = { activeStep, isFacade: topIsFacade, isSelected: topIsSelected, onSelectSelf: topOnSelectSelf };
  // Tipas 4 turi antrą, statmeną GALINĘ juostą (riser) — ji stovi UŽ pačios dangos
  // plokštės (žr. riserZ žemiau), tad fasado/karkaso poslinkis jai NETAIKOMAS (ji
  // visada lieka toje pačioje vietoje, nepriklausomai nuo pagrindinės plokštės
  // perjungimo) — vis tiek paspaudžiama/pažymima kaip to pačio vieneto dalis.
  const primaryToggleProps = { ...toggleProps, showToggleButton: true, onToggleFront: topOnToggleFront };

  // Dvigubo stogelio (3) ir Karūnos/Cokolio (4) APATINĖ danga ("stogelis po
  // karūna"/antrasis sluoksnis) yra STRUKTŪRINĖ plokštė tarp šonų, UŽ durelių —
  // kaip bet kuris kitas korpuso skydas — NE dekoratyvinė flush/ant-šonų danga,
  // kaip viršutinė. ClickUp "2.1 Baldas. Stogelis (dvigubas)/(Karūna/Cokolis).
  // Apatinė plokštė tarp šonų ir mažesnio gylio": plotis — ĮLEISTAS tarp šonų (ta
  // pati taisyklė, kaip tipo 1 "Tarp šonų"), gylis — UŽ durelių, be FRONT_OVERHANG
  // (ta pati taisyklė, kaip tipų 5/6/7 "Nuleistas"). Naudoja TĄ PATĮ topDepthDelta
  // (fasado/karkaso perjungiklį), kaip ir viršutinė danga — visa Stogelio plokštė
  // lieka VIENAS pažymimas/perjungiamas vienetas. Tipo 4 riserių (karūnos juostų)
  // pozicija NELIEČIAMA — jos toliau remiasi TIKRU/pilnu w/cx/dEff/dPosZEff/halfW,
  // nes karūna yra matoma dekoratyvinė dalis prie TIKRO korpuso krašto, ne
  // struktūrinė danga.
  const underLeftInset = isGroupLeftEdge ? safeWallThickness : 0;
  const underRightInset = isGroupRightEdge ? safeWallThickness : 0;
  const wUnder = Math.max(MIN_DIMENSION, safeTotalWidth - underLeftInset - underRightInset);
  const cxUnder = (underLeftInset - underRightInset) / 2;
  const dEffUnder = safeDepth + topDepthDelta;
  const dPosZUnder = topDepthDelta / 2;

  // 1: Tarp šonų — plokštė įleista tarp šonų (siauresnė už korpusą), su nedideliu
  // priekiniu overhang, flush su galu ir abiem šonais. Y: viršutinis paviršius
  // TIKSLIAI ties safeTopY (žr. topY={totalHeight} FurnitureGroup.jsx) — TA PATI
  // konvencija, kokią SingleCabinet3D.jsx naudoja normaliam "Top wall" skydui
  // (position Y = cabHeight - wallThickness/2, t.y. kabo ŽEMYN nuo tikro viršaus,
  // NE kyla aukštyn virš jo). PATAISYTA: anksčiau čia (ir visuose tipuose žemiau)
  // buvo `safeTopY + safeWallThickness/2` — plokštė kildavo AUKŠČIAU už baldo
  // nominalų viršų, palikdama safeWallThickness pločio tuščią tarpą virš karkaso
  // šonų/pertvarų, kaip kad jie "kabėtų ore" (žr. vartotojo screenshot — matomas
  // šviesus tarpas tarp Stogelio ir šoninių plokščių).
  if (type === 1) {
    return (
      <TopBox args={[w, safeWallThickness, dEff]} position={[cx, safeTopY - safeWallThickness / 2, dPosZEff]} color={color} showEdges={showEdges} {...primaryToggleProps} />
    );
  }

  // 2: Ant šonų — lygi su korpuso pločiu (dengia abu šonus iš viršaus, be pločio
  // overhang — žr. ZIP "Stogelis2 - Ant šonų2.jpg", plokštė flush su korpuso kraštais).
  if (type === 2) {
    return (
      <TopBox args={[w, safeWallThickness, dEff]} position={[cx, safeTopY - safeWallThickness / 2, dPosZEff]} color={color} showEdges={showEdges} {...primaryToggleProps} />
    );
  }

  // 3: Dvigubas stogelis — VIRŠUTINĖ plokštė kaip (2) (žr. ZIP "Stogelis3 - Dvigubas
  // stogelis" — plona papildoma linija lygiai viršuje = du sluoksniai, NE nuleisti
  // kojeliai), APATINĖ (antra) plokštė — struktūrinė, įleista tarp šonų + už durelių
  // (žr. wUnder/dEffUnder aukščiau, ClickUp "Stogelis (dvigubas). Apatinė plokštė
  // tarp šonų ir mažesnio gylio").
  if (type === 3) {
    return (
      <group>
        <TopBox args={[w, safeWallThickness, dEff]} position={[cx, safeTopY - safeWallThickness / 2, dPosZEff]} color={color} showEdges={showEdges} {...primaryToggleProps} />
        <TopBox args={[wUnder, safeWallThickness, dEffUnder]} position={[cxUnder, safeTopY - safeWallThickness * 1.5, dPosZUnder]} color={color} showEdges={showEdges} {...toggleProps} />
      </group>
    );
  }

  // 4: Karūna / Cokolis — pagrindinė danga DABAR struktūrinė, įleista tarp šonų + už
  // durelių (žr. wUnder/dEffUnder aukščiau, ta pati taisyklė, kaip tipo 3 apatinė
  // plokštė — ClickUp "Stogelis (Karūna/Cokolis). Apatinė plokštė tarp šonų ir
  // mažesnio gylio"), PLIUS TRYS statmenos juostos, stovinčios ANT jos viršaus
  // (dekoratyvinis, matomas rėmas — TOLIAU prie TIKRO/pilno korpuso krašto, žr.
  // dEff/halfW žemiau, NEpaliesti): VIENA PRIEKYJE ir DVI ŠONUOSE (kairėje/
  // dešinėje), kiekviena pakelta per `height`
  // (dialogo aukščio laukas) — U formos rėmas (žiūrint iš viršaus), atviras tik
  // gale (jokios galinės juostos nėra). PATAISYTA: ankstesnė versija (pagal ZIP
  // brėžinio interpretaciją) turėjo VIENĄ juostą TIK gale — vartotojas tiesiogiai
  // paaiškino tikrą dizainą: "ant virsutines baldo plokstes uszideti viena
  // priekine ir dvi sonines, pagal nurodyta auksti is dialog lango".
  if (type === 4) {
    const riserY = safeTopY + safeHeight / 2;
    // Šoninės juostos siekia TĄ PATĮ priekinį kraštą (dEff/dPosZEff — dangos
    // plokštės pilnas gylis su overhang), kad susitiktų su priekine juosta kampe
    // be tarpo; priekinė juosta įgilinta per pusę savo storio nuo to krašto, kad
    // jos IŠORINIS paviršius (ne centras) liktų lygiai ties dangos plokštės kraštu.
    const frontRiserZ = dPosZEff + dEff / 2 - safeWallThickness / 2;
    const leftRiserX = -halfW + safeWallThickness / 2;
    const rightRiserX = halfW - safeWallThickness / 2;
    // Šoninės juostos žymi TIKRĄ baldo kraštą (U formos rėmo "galai") — vidinėje
    // run'ų riboje (kai šis kvietimas neapima to TIKRO krašto) jų NĖRA, kitaip
    // kiekvienas run'as pieštų savo juostą ties kiekvienu VIDINIU susiliejimu.
    // Priekinė juosta TOLIAU piešiama kiekvienam run'ui (natūraliai susilieja be
    // tarpo/laiptelio su kaimyniniu run'u per w, kaip ir pati danga).
    return (
      <group>
        <TopBox args={[wUnder, safeWallThickness, dEffUnder]} position={[cxUnder, safeTopY - safeWallThickness / 2, dPosZUnder]} color={color} showEdges={showEdges} {...primaryToggleProps} />
        <TopBox args={[w, safeHeight, safeWallThickness]} position={[cx, riserY, frontRiserZ]} color={color} showEdges={showEdges} {...toggleProps} />
        {isGroupLeftEdge && <TopBox args={[safeWallThickness, safeHeight, dEff]} position={[leftRiserX, riserY, dPosZEff]} color={color} showEdges={false} {...toggleProps} />}
        {isGroupRightEdge && <TopBox args={[safeWallThickness, safeHeight, dEff]} position={[rightRiserX, riserY, dPosZEff]} color={color} showEdges={false} {...toggleProps} />}
      </group>
    );
  }

  // 5: Nuleistas — plokštė ĮLEISTA tarp šoninių sienelių (kaip (1), NE overhang), tik
  // pastumta žemyn per `height` — patys šonai (jau piešiami kitur, ne šio komponento)
  // natūraliai lieka aukščiau už įleistą plokštę, sukurdami "rėmelio" efektą.
  if (type === 5) {
    return (
      <TopBox args={[w, safeWallThickness, dEff]} position={[cx, safeTopY - yDrop - safeWallThickness / 2, dPosZEff]} color={color} showEdges={showEdges} {...primaryToggleProps} />
    );
  }

  // 6/7: Nuleistas kairys/dešinys — VIENA ištisinė plokštė, nuleista per `height`
  // VISU ilgiu (kaip (5), VIENAME lygyje, be laiptelio viduryje), bet su ASIMETRIŠKU
  // pločiu: flush pusėje siekia TIKSLIAI iki korpuso krašto (be overhang/inset —
  // "priklauso" tai pusei), priešingoje (inset) pusėje siekia tik sienelės vidinį
  // paviršių (įleista per safeWallThickness). PATAISYTA (3x): (1) kairė/dešinė buvo
  // sukeistos — žr. failo viršaus komentarą (ZIP "Stogelis6 - Ant kairio šono" rodo
  // kyšulį/nuleidimą DEŠINĖJE, ne kairėje); (2) pašalintas pločio overhang flush
  // pusėje; (3) PATI PLOKŠTĖ grąžinta į VIENĄ ištisinį gabalą VIENAME lygyje —
  // trumpam buvo bandyta skaidyti į DVI dalis skirtinguose Y (flush pusė nenuleista,
  // inset nuleista), kad flush pusėje plokštė nebesikirstų su pilno aukščio sienele,
  // bet vartotojas paaiškino, kad TOKS skaidymas neteisingas: "stogelis nusileidzia
  // per visa ilgi" (plokštė VISADA viena, nuleista per visą ilgį) — asimetrija
  // (kodėl vienas kampas lygus, kitas su iškyša) kyla NE iš pačios plokštės, o iš
  // TIKROS IŠORINĖS ŠONINĖS SIENELĖS aukščio: flush pusėje sienelė PATI sutrumpėja
  // iki nuleistos plokštės lygio (žr. SingleCabinet3D.jsx leftOuterFlushDrop/
  // rightOuterFlushDrop), inset pusėje sienelė lieka pilno aukščio (sukuria
  // matomą iškyšą/"nuleidimo" efektą TEN).
  if (type === 6 || type === 7) {
    return (
      <TopBox args={[w, safeWallThickness, dEff]} position={[cx, safeTopY - yDrop - safeWallThickness / 2, dPosZEff]} color={color} showEdges={showEdges} {...primaryToggleProps} />
    );
  }

  // 8/9: Ant dešinio/kairio šono — geometriškai TAS PATS, kas (2) (žr. failo viršaus
  // komentarą — Stogelis niekada nekyšo už baldo šoninių sienelių, nepriklausomai
  // nuo tipo pavadinimo; papildomą plotį prideda tik atskiras "Kairys/Dešinys
  // šonas" funkcionalumas, ne Stogelis).
  return (
    <TopBox args={[w, safeWallThickness, dEff]} position={[cx, safeTopY - safeWallThickness / 2, dPosZEff]} color={color} showEdges={showEdges} {...primaryToggleProps} />
  );
};

export default UnifiedTopPanel;
